// @flow
import axios from 'axios';

import SavedTranscriptTypes from '../action-types/saved-transcript';
import { selectLastReadTimestamp, selectLoading, selectTranscripts } from '../selectors/saved-transcript';
import { selectUser } from '../selectors/user';

import firebase, { authInstance, restName } from '../firebase';
import { deleteTranscriptFromUser, updateTranscriptTitleForUser } from '../firebase/saved-transcript';
import { SavedTranscript } from '../types/saved-transcript';
import { cloudTranscriptsEndpoint } from '../utils/http';

export const fetchTranscriptsRequest = () => ({
  type: SavedTranscriptTypes.FETCH_TRANSCRIPTS_REQUEST,
});

export const fetchTranscriptsSuccess = (transcripts: SavedTranscript[]) => ({
  type: SavedTranscriptTypes.FETCH_TRANSCRIPTS_SUCCESS,
  payload: transcripts,
});

export const fetchTranscriptsFailure = (errorMessage: string) => ({
  type: SavedTranscriptTypes.FETCH_TRANSCRIPTS_FAILURE,
  payload: errorMessage,
});

export const setHasMoreTranscripts = (hasMore: boolean) => ({
  type: SavedTranscriptTypes.SET_HAS_MORE_TRANSCRIPTS,
  payload: hasMore,
});

export const fetchTranscriptsRequestAsync = () => {
  // $FlowFixMe
  return async (dispatch, getState) => {
    try {
      const state = getState();

      //  Don't dispatch another action if already fetching transcripts
      const loading = selectLoading(state);
      if (loading) return;

      dispatch(fetchTranscriptsRequest());

      const firebaseUser = firebase.auth().currentUser;
      if (!firebaseUser) return;

      const user = selectUser(state);

      let endpoint = `${cloudTranscriptsEndpoint}?avaId=${user.avaId}&firebaseAuthID=${firebaseUser.uid}&limit=10`;

      const transcripts: SavedTranscript[] = selectTranscripts(state);
      if (transcripts.length > 0) {
        const lastReadTimestamp = selectLastReadTimestamp(state);
        endpoint += `&before=${lastReadTimestamp}`;
      }

      const token = await authInstance.currentUser.getIdToken();
      const { data } = await axios.get(
        endpoint,
        {},
        {
          auth: { username: restName, password: token },
        }
      );

      if (data.length) {
        dispatch(fetchTranscriptsSuccess(data));
      } else {
        dispatch(setHasMoreTranscripts(false));
      }
    } catch (error) {
      dispatch(fetchTranscriptsFailure(error.message));
    }
  };
};

export const deleteTranscriptRequest = () => ({
  type: SavedTranscriptTypes.DELETE_TRANSCRIPT_REQUEST,
});

export const deleteTranscriptSuccess = (deletedTranscript: SavedTranscript) => ({
  type: SavedTranscriptTypes.DELETE_TRANSCRIPT_SUCCESS,
  payload: deletedTranscript,
});

export const deleteTranscriptFailure = (errorMessage: string) => ({
  type: SavedTranscriptTypes.DELETE_TRANSCRIPT_FAILURE,
  payload: errorMessage,
});

export const deleteTranscriptRequestAsync = ({ avaId, transcript }: { avaId: string, transcript: SavedTranscript }) => {
  // $FlowFixMe
  return async dispatch => {
    try {
      dispatch(deleteTranscriptRequest());

      await deleteTranscriptFromUser({
        userId: avaId,
        roomId: transcript.roomId,
      });

      dispatch(deleteTranscriptSuccess(transcript));
    } catch (err) {
      dispatch(deleteTranscriptFailure(err.message));
    }
  };
};

export const setSelectedTranscriptId = (id: string) => ({
  type: SavedTranscriptTypes.SET_SELECTED_TRANSCRIPT_ID,
  payload: id,
});

export const resetTranscripts = () => ({
  type: SavedTranscriptTypes.RESET_TRANSCRIPTS,
});

export const updateTranscriptTitleRequest = () => ({
  type: SavedTranscriptTypes.UPDATE_TRANSCRIPT_TITLE_REQUEST,
});

export const updateTranscriptTitleSuccess = (updatedTranscript: SavedTranscript) => ({
  type: SavedTranscriptTypes.UPDATE_TRANSCRIPT_TITLE_SUCCESS,
  payload: updatedTranscript,
});

export const updateTranscriptTitleFailure = (errorMessage: string) => ({
  type: SavedTranscriptTypes.UPDATE_TRANSCRIPT_TITLE_FAILURE,
  payload: errorMessage,
});

export const updateTranscriptTitleRequestAsync = ({
  avaId,
  transcript,
  title,
}: {
  avaId: string,
  transcript: SavedTranscript,
  title: string,
}) => {
  // $FlowFixMe
  return async dispatch => {
    try {
      dispatch(updateTranscriptTitleRequest());

      await updateTranscriptTitleForUser({
        userId: avaId,
        roomId: transcript.roomId,
        title,
      });

      const updatedTranscript = { ...transcript, title };
      dispatch(updateTranscriptTitleSuccess(updatedTranscript));
    } catch (err) {
      dispatch(updateTranscriptTitleFailure(err.message));
    }
  };
};

export const addNewTranscript = (newTranscript: SavedTranscript) => ({
  type: SavedTranscriptTypes.ADD_NEW_TRANSCRIPT,
  payload: newTranscript,
});
